"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { REPLY_OUTCOME_CONFIG } from "../constants";
import type { ReplyOutcomeData, ReplyOutcome } from "../types";

interface ReplyOutcomesChartProps {
  data: ReplyOutcomeData[];
}

function buildChartConfig(data: ReplyOutcomeData[]) {
  const config: Record<string, { label: string; color: string }> = {};
  for (const item of data) {
    const cfg = REPLY_OUTCOME_CONFIG[item.outcome as ReplyOutcome];
    if (cfg) {
      config[item.outcome] = { label: cfg.label, color: cfg.color };
    }
  }
  return config;
}

export function ReplyOutcomesChart({ data }: ReplyOutcomesChartProps) {
  const chartConfig = buildChartConfig(data);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Reply Outcomes</CardTitle>
        <p className="text-xs text-muted-foreground">
          Distribution of auto-reply results
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <ChartContainer config={chartConfig} className="h-[220px] w-[220px] flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <ChartTooltip content={<ChartTooltipContent />} />
                <Pie
                  data={data as unknown as Record<string, unknown>[]}
                  dataKey="count"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  strokeWidth={2}
                >
                  {data.map((entry) => (
                    <Cell
                      key={entry.outcome}
                      fill={REPLY_OUTCOME_CONFIG[entry.outcome as ReplyOutcome]?.color ?? "#6b7280"}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>

          {/* Legend */}
          <div className="flex flex-col gap-1.5 min-w-0">
            {data.map((item) => {
              const cfg = REPLY_OUTCOME_CONFIG[item.outcome as ReplyOutcome];
              const pctLabel = item.pct === 0 && item.count > 0 ? "<1" : String(item.pct);
              return (
                <div key={item.outcome} className="flex items-center gap-2 text-sm">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: cfg?.color ?? "#6b7280" }}
                  />
                  <span className="text-muted-foreground truncate">
                    {item.label}
                  </span>
                  <span className="ml-auto font-medium tabular-nums whitespace-nowrap">
                    {item.count} ({pctLabel}%)
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
